import React from 'react';
import { FileText } from 'lucide-react';

const Terms = () => {
  return (
    <div className="min-h-screen bg-white">
      <section className="bg-gradient-to-br from-[#F4F6F8] to-white py-20">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center justify-center mb-6">
            <FileText className="text-[#0F4C81]" size={48} />
          </div>
          <h1 className="text-5xl font-bold text-[#12324A] mb-6 text-center">
            Terms of Service
          </h1>
          <p className="text-lg text-[#5F6C7B] text-center">
            Last Updated: {new Date().toLocaleDateString()}
          </p>
        </div>
      </section>

      <section className="py-12 bg-white">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="prose prose-lg max-w-none">
            <div className="bg-[#F4F6F8] rounded-xl p-6 mb-8">
              <p className="text-[#5F6C7B] m-0">
                These Terms of Service govern your use of TouchScreenMD services. By accessing or using our services, you agree to be bound by these terms.
              </p>
            </div>

            <h2 className="text-3xl font-bold text-[#12324A] mt-12 mb-4">1. Acceptance of Terms</h2>
            <p className="text-[#5F6C7B] mb-6">
              By creating an account or using TouchScreenMD services, you acknowledge that you have read, understood, and agree to be bound by these Terms of Service and our Privacy Policy. If you do not agree to these terms, you may not use our services.
            </p>

            <h2 className="text-3xl font-bold text-[#12324A] mt-12 mb-4">2. Eligibility</h2>
            <p className="text-[#5F6C7B] mb-4">
              To use TouchScreenMD services, you must:
            </p>
            <ul className="space-y-2 mb-6">
              <li className="text-[#5F6C7B]">Be at least 18 years old or have parental consent</li>
              <li className="text-[#5F6C7B]">Be physically located in a state where our providers are licensed</li>
              <li className="text-[#5F6C7B]">Provide accurate and complete information</li>
              <li className="text-[#5F6C7B]">Have the legal capacity to enter into binding contracts</li>
            </ul>

            <h2 className="text-3xl font-bold text-[#12324A] mt-12 mb-4">3. Services Provided</h2>
            <p className="text-[#5F6C7B] mb-4">
              TouchScreenMD provides access to telehealth services through licensed healthcare providers. Our services include:
            </p>
            <ul className="space-y-2 mb-6">
              <li className="text-[#5F6C7B]">Virtual medical consultations via secure video</li>
              <li className="text-[#5F6C7B]">Diagnosis and treatment recommendations</li>
              <li className="text-[#5F6C7B]">Prescription services when medically appropriate</li>
              <li className="text-[#5F6C7B]">Follow-up care and support</li>
            </ul>

            <h2 className="text-3xl font-bold text-[#12324A] mt-12 mb-4">4. Not for Emergencies</h2>
            <div className="bg-red-50 border-2 border-red-200 rounded-xl p-6 mb-6">
              <p className="text-[#5F6C7B] m-0">
                <strong className="text-[#12324A]">IMPORTANT:</strong> TouchScreenMD is not designed for medical emergencies. If you are experiencing a medical emergency, including but not limited to chest pain, severe shortness of breath, stroke symptoms, or major bleeding, call 911 or go to the nearest emergency room immediately.
              </p>
            </div>

            <h2 className="text-3xl font-bold text-[#12324A] mt-12 mb-4">5. Provider-Patient Relationship</h2>
            <p className="text-[#5F6C7B] mb-6">
              When you consult with a provider through TouchScreenMD, a provider-patient relationship is established for the purpose of that specific consultation. However, TouchScreenMD providers are not intended to replace your primary care physician or serve as your sole healthcare provider.
            </p>

            <h2 className="text-3xl font-bold text-[#12324A] mt-12 mb-4">6. User Responsibilities</h2>
            <p className="text-[#5F6C7B] mb-4">
              You agree to:
            </p>
            <ul className="space-y-2 mb-6">
              <li className="text-[#5F6C7B]">Provide accurate and complete health information</li>
              <li className="text-[#5F6C7B]">Keep your account credentials confidential</li>
              <li className="text-[#5F6C7B]">Use services only for lawful purposes</li>
              <li className="text-[#5F6C7B]">Respect the time and expertise of healthcare providers</li>
              <li className="text-[#5F6C7B]">Pay all applicable fees for services rendered</li>
              <li className="text-[#5F6C7B]">Notify us of any changes to your health information</li>
            </ul>

            <h2 className="text-3xl font-bold text-[#12324A] mt-12 mb-4">7. Payment and Billing</h2>
            <p className="text-[#5F6C7B] mb-6">
              You are responsible for all charges associated with your use of TouchScreenMD services. This includes copays, membership fees, or per-visit charges as applicable. We may bill your insurance provider on your behalf, but you remain responsible for any amounts not covered by insurance.
            </p>

            <h2 className="text-3xl font-bold text-[#12324A] mt-12 mb-4">8. Prescriptions</h2>
            <p className="text-[#5F6C7B] mb-6">
              Providers may prescribe medications when medically appropriate and permitted by law. Not all conditions can be treated via telehealth, and some medications (including controlled substances) may not be available through our platform. The decision to prescribe medication is at the sole discretion of the provider.
            </p>

            <h2 className="text-3xl font-bold text-[#12324A] mt-12 mb-4">9. Limitation of Liability</h2>
            <p className="text-[#5F6C7B] mb-6">
              TouchScreenMD provides a platform connecting patients with licensed healthcare providers. We do not practice medicine and are not responsible for the medical advice, diagnosis, or treatment provided by individual healthcare providers. To the fullest extent permitted by law, TouchScreenMD shall not be liable for any indirect, incidental, special, or consequential damages.
            </p>

            <h2 className="text-3xl font-bold text-[#12324A] mt-12 mb-4">10. Privacy and Confidentiality</h2>
            <p className="text-[#5F6C7B] mb-6">
              Your privacy is important to us. Please review our Privacy Policy and Notice of Privacy Practices to understand how we collect, use, and protect your personal health information.
            </p>

            <h2 className="text-3xl font-bold text-[#12324A] mt-12 mb-4">11. Termination</h2>
            <p className="text-[#5F6C7B] mb-6">
              We reserve the right to suspend or terminate your account if you violate these Terms of Service or engage in fraudulent, abusive, or illegal activity. You may cancel your account at any time by contacting customer support.
            </p>

            <h2 className="text-3xl font-bold text-[#12324A] mt-12 mb-4">12. Changes to Terms</h2>
            <p className="text-[#5F6C7B] mb-6">
              We may modify these Terms of Service at any time. We will notify you of significant changes by posting the updated terms on our website. Your continued use of our services after changes are posted constitutes acceptance of the modified terms.
            </p>

            <h2 className="text-3xl font-bold text-[#12324A] mt-12 mb-4">13. Contact Information</h2>
            <p className="text-[#5F6C7B] mb-4">
              If you have questions about these Terms of Service, please contact us:
            </p>
            <div className="bg-[#F4F6F8] rounded-xl p-6">
              <p className="text-[#5F6C7B] m-0"><strong>Email:</strong> support@touchscreenmd.com</p>
              <p className="text-[#5F6C7B] m-0 mt-2"><strong>Phone:</strong> 1-800-TOUCH-MD</p>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Terms;
